import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { sessionApi } from '@/api/endpoints/session';
import { ApiError } from '@/api/client';

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => sessionApi.login(email, password),
    onSuccess: (user) => {
      // Usa o usuário retornado pelo próprio POST /session em vez de refazer a
      // consulta. Evita a corrida (cookie ainda não propagado → sessão null →
      // volta pro login) que fazia precisar logar várias vezes.
      queryClient.setQueryData(['session'], user);
      navigate('/dashboard', { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-4">
      <img
        src="/fismatek-logo.png"
        alt="Fismatek"
        className="h-32 w-32 object-contain"
      />
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Entrar</CardTitle>
          <CardDescription>Acesse com suas credenciais.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                E-mail
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {login.error && (
              <p className="text-sm text-destructive">
                {login.error instanceof ApiError && login.error.status === 401
                  ? 'E-mail ou senha inválidos.'
                  : 'Falha ao conectar. Verifique se o servidor está acessível.'}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <div className="max-w-sm text-center text-xs text-muted-foreground">
        <p>Problemas para acessar?</p>
        <p>Entre em contato com o suporte técnico.</p>
      </div>
    </div>
  );
}
